import { useState, useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from '@/components/ui/form';
import {
  useTrainingTypes,
  useCreateTrainingSession,
  useUpdateTrainingSession,
  getTrainingStatusLabel,
} from '@/hooks/useRPL';
import { trainingSessionFormSchema, type TrainingSessionFormData } from '@/schemas/rplSchemas';
import type { TrainingSession, TrainingStatus } from '@/types/rpl';
import { Loader2 } from 'lucide-react';

interface TrainingSessionFormDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  session?: TrainingSession | null;
}

const STATUSES: TrainingStatus[] = ['scheduled', 'in_progress', 'completed', 'cancelled'];

export function TrainingSessionFormDialog({ open, onOpenChange, session }: TrainingSessionFormDialogProps) {
  const { data: trainingTypes } = useTrainingTypes(true);
  const createMutation = useCreateTrainingSession();
  const updateMutation = useUpdateTrainingSession();
  const [loading, setLoading] = useState(false);

  const form = useForm<TrainingSessionFormData>({
    resolver: zodResolver(trainingSessionFormSchema),
    defaultValues: {
      training_type_id: '',
      title: '',
      description: '',
      instructor: '',
      location: '',
      scheduled_date: '',
      start_time: '',
      end_time: '',
      max_participants: null,
      status: 'scheduled',
      materials_url: '',
      notes: '',
    },
  });

  useEffect(() => {
    if (open) {
      form.reset({
        training_type_id: session?.training_type_id || '',
        title: session?.title || '',
        description: session?.description || '',
        instructor: session?.instructor || '',
        location: session?.location || '',
        scheduled_date: session?.scheduled_date || '',
        start_time: session?.start_time || '',
        end_time: session?.end_time || '',
        max_participants: session?.max_participants || null,
        status: session?.status || 'scheduled',
        materials_url: session?.materials_url || '',
        notes: session?.notes || '',
      });
    }
  }, [open, session, form]);

  // Auto-fill title from training type
  const watchTrainingType = form.watch('training_type_id');
  useEffect(() => {
    if (watchTrainingType && !session && !form.getValues('title')) {
      const selectedType = trainingTypes?.find((t) => t.id === watchTrainingType);
      if (selectedType) {
        form.setValue('title', selectedType.name);
      }
    }
  }, [watchTrainingType, trainingTypes, session, form]);

  const onSubmit = async (data: TrainingSessionFormData) => {
    setLoading(true);
    try {
      const payload = {
        training_type_id: data.training_type_id,
        title: data.title,
        description: data.description || null,
        instructor: data.instructor || null,
        location: data.location || null,
        scheduled_date: data.scheduled_date,
        start_time: data.start_time || null,
        end_time: data.end_time || null,
        max_participants: data.max_participants || null,
        status: data.status,
        materials_url: data.materials_url || null,
        notes: data.notes || null,
      };

      if (session) {
        await updateMutation.mutateAsync({ id: session.id, data: payload });
      } else {
        await createMutation.mutateAsync(payload);
      }

      onOpenChange(false);
      form.reset();
    } catch (error) {
      // Error handled by mutation
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>{session ? 'Editar Sesion' : 'Nueva Sesion de Formacion'}</DialogTitle>
        </DialogHeader>

        <Form {...form}>
          <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
            <FormField
              control={form.control}
              name="training_type_id"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Tipo de formacion *</FormLabel>
                  <Select onValueChange={field.onChange} value={field.value}>
                    <FormControl>
                      <SelectTrigger>
                        <SelectValue placeholder="Seleccionar..." />
                      </SelectTrigger>
                    </FormControl>
                    <SelectContent>
                      {trainingTypes?.map((type) => (
                        <SelectItem key={type.id} value={type.id}>
                          {type.name} {type.is_mandatory && '(Obligatoria)'}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="title"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Titulo *</FormLabel>
                  <FormControl>
                    <Input {...field} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <div className="grid grid-cols-2 gap-4">
              <FormField
                control={form.control}
                name="scheduled_date"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Fecha *</FormLabel>
                    <FormControl>
                      <Input type="date" {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="status"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Estado</FormLabel>
                    <Select onValueChange={field.onChange} value={field.value}>
                      <FormControl>
                        <SelectTrigger>
                          <SelectValue />
                        </SelectTrigger>
                      </FormControl>
                      <SelectContent>
                        {STATUSES.map((status) => (
                          <SelectItem key={status} value={status}>
                            {getTrainingStatusLabel(status)}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="start_time"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Hora inicio</FormLabel>
                    <FormControl>
                      <Input type="time" {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="end_time"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Hora fin</FormLabel>
                    <FormControl>
                      <Input type="time" {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <FormField
                control={form.control}
                name="instructor"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Instructor</FormLabel>
                    <FormControl>
                      <Input {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="location"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Ubicacion</FormLabel>
                    <FormControl>
                      <Input {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>

            <FormField
              control={form.control}
              name="max_participants"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Maximo participantes</FormLabel>
                  <FormControl>
                    <Input
                      type="number"
                      {...field}
                      value={field.value ?? ''}
                      onChange={(e) => field.onChange(e.target.value ? Number(e.target.value) : null)}
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="description"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Descripcion</FormLabel>
                  <FormControl>
                    <Textarea {...field} rows={2} />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => onOpenChange(false)} disabled={loading}>
                Cancelar
              </Button>
              <Button type="submit" disabled={loading}>
                {loading && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
                {session ? 'Actualizar' : 'Crear'}
              </Button>
            </DialogFooter>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  );
}
